import { Component, inject, input, output, signal } from '@angular/core';
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { SiteMessage } from "../../models/site-message";
import { Timestamp } from "@angular/fire/firestore";
import { MatSnackBar } from "@angular/material/snack-bar";
import { RepoService } from "../../services/repo.service";
import { AuthService } from "../../services/auth.service";
import { NgOptimizedImage } from "@angular/common";
import { MatDrawer, MatDrawerContainer } from "@angular/material/sidenav";
import { PreviousSiteMessagesComponent } from "../previous-site-messages/previous-site-messages.component";
import { debounceTime, distinctUntilChanged, of, switchMap } from "rxjs";
import { Site } from "../../models/site";


@Component({
  selector: 'app-create-site-message-drawer',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgOptimizedImage,
    MatDrawer,
    MatDrawerContainer,
    PreviousSiteMessagesComponent,
  ],
  templateUrl: './create-site-message-drawer.component.html',
  styleUrl: './create-site-message-drawer.component.css'
})
export class CreateSiteMessageDrawerComponent {

  authService = inject(AuthService)
  snackBar = inject(MatSnackBar)
  repoService = inject(RepoService)
  fb = inject(FormBuilder)


  selectedSite = signal<Site | null>(null)
  isTranslating = signal(false)

  searchControl = new FormControl('');
  private sitesStream$ = this.searchControl.valueChanges.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    switchMap(term => {
      if (term && term.length >= 2) {
        return this.repoService.getSite(term)
      }
      return of([])
    })
  );
  sites = toSignal(this.sitesStream$, { initialValue: [] as Site[] });

  languageDefinitions: { name: string, isoCode: string, flaggUrl: string }[] = [
    { name: "Swedish", isoCode: "sv", flaggUrl: "assets/ic_sweden.svg" },
    { name: "Norwegian", isoCode: "no", flaggUrl: "assets/ic_norway.svg" },
    { name: "German", isoCode: "de", flaggUrl: "assets/ic_germany.svg" },
    { name: "English", isoCode: "en", flaggUrl: "assets/ic_uk.png" }
  ];
  languageForm = this.fb.array([
    ...this.languageDefinitions.map(langDef => this.fb.group({
      name: [langDef.name],
      iosCode: [langDef.isoCode],
      flagUrl: [langDef.flaggUrl],
      title: ["", Validators.required],
      message: ["", Validators.required],
    }))
  ])
  currentMessages = input<SiteMessage[]>([])
  selectedSiteMessage = input<SiteMessage | null>(null);
  onCloseClicked = output();

  selectSite(site: Site) {
    this.selectedSite.set(site)
    // this.searchControl.setValue('', {emitEvent: false});
  }

  async createSiteMessageClicked() {

    const messageForSiteExists = this.currentMessages().some(message => message.id === this.selectedSite().id);

    if (messageForSiteExists) {
      this.snackBar.open("Site has already an active message", "close")
    } else {
      let siteMessage = new SiteMessage(this.selectedSite().id, this.languageForm.controls[0].value.message, this.languageForm.controls[0].value.title, Timestamp.now(), null, this.authService.userEmail, this.selectedSite().name, this.selectedSite().shortAddress, this.languageForm.controls[0].value.title, this.languageForm.controls[0].value.message, this.languageForm.controls[1].value.title, this.languageForm.controls[1].value.message, this.languageForm.controls[2].value.title, this.languageForm.controls[2].value.message, this.languageForm.controls[3].value.title, this.languageForm.controls[3].value.message);
      await this.repoService.createSiteMessage(siteMessage);


      for (let i = 0; i < this.languageDefinitions.length; i++) {
        this.languageForm.controls[i].controls.title.reset();
        this.languageForm.controls[i].controls.message.reset();
        this.selectedSite.set(null)
      }

      this.onCloseClicked.emit();
    }

  }

  copyMessage(siteMessage: SiteMessage) {

    if (siteMessage.swedishMessage) {
      this.languageForm.controls[0].controls.title.setValue(siteMessage.swedishTitle)
      this.languageForm.controls[0].controls.message.setValue(siteMessage.swedishMessage)
      this.languageForm.controls[1].controls.title.setValue(siteMessage.norwegianTitle)
      this.languageForm.controls[1].controls.message.setValue(siteMessage.norwegianMessage)
      this.languageForm.controls[2].controls.title.setValue(siteMessage.germanTitle)
      this.languageForm.controls[2].controls.message.setValue(siteMessage.germanMessage)
      this.languageForm.controls[3].controls.title.setValue(siteMessage.englishTitle)
      this.languageForm.controls[3].controls.message.setValue(siteMessage.englishMessage)
    } else {
      this.languageForm.controls[0].controls.title.setValue(siteMessage.title)
      this.languageForm.controls[0].controls.message.setValue(siteMessage.message)
    }

  }

  async translate() {
    let title: string;
    let message: string;

    let sourceLanguage = "sv";
    if (this.languageForm.controls[0].value.message) {
      sourceLanguage = "sv"
    } else if (this.languageForm.controls[1].value.message) {
      sourceLanguage = "no"
    } else if (this.languageForm.controls[2].value.message) {
      sourceLanguage = "de"
    } else if (this.languageForm.controls[3].value.message) {
      sourceLanguage = "en"
    } else {
      return;
    }

    switch (sourceLanguage) {
      case "en":
        title = this.languageForm.controls[3].value.title
        message = this.languageForm.controls[3].value.message
        break;

      case "sv":
        title = this.languageForm.controls[0].value.title
        message = this.languageForm.controls[0].value.message
        break;
      case "no":
        title = this.languageForm.controls[1].value.title
        message = this.languageForm.controls[1].value.message
        break;

      default:
        title = this.languageForm.controls[2].value.title
        message = this.languageForm.controls[2].value.message
        break;
    }

    this.isTranslating.set(true);
    try {
      let translations = await this.repoService.translateInAPPMessage(title, message, "")


      this.languageForm.controls[3].controls.title.setValue(translations.englishTitle)
      this.languageForm.controls[3].controls.message.setValue(translations.englishMessage)

      this.languageForm.controls[0].controls.title.setValue(translations.swedishTitle)
      this.languageForm.controls[0].controls.message.setValue(translations.swedishMessage)

      this.languageForm.controls[1].controls.title.setValue(translations.norwegianTitle)
      this.languageForm.controls[1].controls.message.setValue(translations.norwegianMessage)


      this.languageForm.controls[2].controls.title.setValue(translations.germanTitle)
      this.languageForm.controls[2].controls.message.setValue(translations.germanMessage)
    } finally {
      this.isTranslating.set(false);
    }
  }

}
